/**
 * Environment-varying segment & survey IDs — the SINGLE source of truth.
 *
 * These IDs differ between the dev and prod databases, so they must never be
 * hardcoded at a call site. Every `VITE_*_SEGMENT_ID` / `VITE_*_SURVEY_CHAT_ENTRY_ID`
 * is read HERE and nowhere else; components import `SEGMENT_IDS` /
 * `SURVEY_CHAT_ENTRY_IDS` instead of re-reading the environment.
 *
 * Why this file exists: the same segment previously carried different fallbacks in
 * different files (NATURAL_ORGANIC was 955 in one chat surface and 222 in two others),
 * so if an env var was ever missing the three surfaces silently disagreed. A single
 * canonical value per segment removes that class of bug.
 *
 * Defaults are the PRODUCTION values, mirroring the backend's "default is prod" rule
 * (see agents/healthcare_chat_v2/config.py). Dev values come from `.env.development`,
 * which `vite build --mode development` / `vite dev` load. Values are authoritative
 * from the backend registry `agents/healthcare_chat_v2/segments.json` (`ids` maps).
 *
 * NOTE: Vite only inlines LITERAL `import.meta.env.VITE_X` accesses at build time —
 * a dynamic `import.meta.env[key]` lookup is NOT replaced and would be undefined in
 * the bundle. That is why each entry spells out its variable explicitly.
 */

/** Parse an env value to a positive int, falling back when unset/blank/invalid. */
const num = (raw: unknown, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const SEGMENT_IDS = {
  // ── Segments whose IDs differ dev vs prod (dev values live in .env.development) ──
  NATURAL_ORGANIC: num(import.meta.env.VITE_NATURAL_ORGANIC_SEGMENT_ID, 955), // dev 222
  FLAVCITY: num(import.meta.env.VITE_FLAVCITY_SEGMENT_ID, 1146), // dev 385
  KNF_SURVEY: num(import.meta.env.VITE_KNF_SURVEY_SEGMENT_ID, 1194), // dev 390
  DOGS_AUDIENCE: num(import.meta.env.VITE_DOGS_AUDIENCE_SEGMENT_ID, 1181), // dev 434
  CATS_AUDIENCE: num(import.meta.env.VITE_CATS_AUDIENCE_SEGMENT_ID, 1182), // dev 435
  JERKY: num(import.meta.env.VITE_JERKY_SEGMENT_ID, 1195), // dev 449
  MONDELEZ: num(import.meta.env.VITE_MONDELEZ_SEGMENT_ID, 1168), // dev 394 — "Cookie Consumers"
  CHOCOLATE: num(import.meta.env.VITE_CHOCOLATE_SEGMENT_ID, 1219), // dev 806 — pladis chocolate/cookie/cracker
  /** Brand cohort: the 12 Dog Owners twins whose primary brand is Freshpet. */
  FRESHPET_DOGS: num(import.meta.env.VITE_FRESHPET_DOGS_SEGMENT_ID, 1220), // dev 807

  // ── Same ID in both environments ──
  SMARTYPANTS: num(import.meta.env.VITE_SMARTYPANTS_SEGMENT_ID, 1167),
  ALT_MILK: num(import.meta.env.VITE_ALT_MILK_SEGMENT_ID, 1169),
  AMY_AUDIENCE: num(import.meta.env.VITE_AMY_AUDIENCE_SEGMENT_ID, 1188),
  /**
   * Breakfast — Yogurt. Has its OWN variable: it previously reused
   * VITE_MONDELEZ_SEGMENT_ID, so a single variable had to supply two different
   * segments and whichever was set won for both.
   */
  BREAKFAST_YOGURT: num(import.meta.env.VITE_BREAKFAST_YOGURT_SEGMENT_ID, 753),

  // ── Marketplace twin-chat segments (currently dev-only: no distinct prod row,
  //    so the default doubles as the prod value). Tracked in frontend issue #256. ──
  SMARTYPANTS_RAW: num(import.meta.env.VITE_SMARTYPANTS_RAW_SEGMENT_ID, 391),
  SMARTYPANTS_SYNTH: num(import.meta.env.VITE_SMARTYPANTS_SYNTH_SEGMENT_ID, 392),
  SMARTYPANTS_DT: num(import.meta.env.VITE_SMARTYPANTS_DT_SEGMENT_ID, 393),
  /** Same underlying segment as MONDELEZ; kept as its own var for the marketplace surface. */
  COOKIE_CONSUMER: num(import.meta.env.VITE_COOKIE_CONSUMER_SEGMENT_ID, 1168), // dev 394
  KNF_DEEP_SURVEY: num(import.meta.env.VITE_KNF_DEEP_SURVEY_SEGMENT_ID, 411),
  KNF_DEEP_OLD_JOINT: num(import.meta.env.VITE_KNF_DEEP_OLD_JOINT_SEGMENT_ID, 446),
  /** Synthetic scale-test segment: 1000 personas in a single segment. */
  MOCK_CHAT_1000: num(import.meta.env.VITE_MOCK_CHAT_1000_SEGMENT_ID, 470),
} as const;

/**
 * Entry survey opened by the "Human Survey Insights" tile, per client.
 *
 * Must stay in sync with SURVEY_CHAT_CLIENTS in the backend
 * (routes_fastapi/survey_chat.py), which still hardcodes these — data-driving that
 * list is part of frontend issue #256.
 */
export const SURVEY_CHAT_ENTRY_IDS = {
  DEFAULT: num(import.meta.env.VITE_SURVEY_CHAT_ENTRY_ID, 154), // Bouillon
  ARCHERJERKY: num(import.meta.env.VITE_ARCHERJERKY_SURVEY_CHAT_ENTRY_ID, 155),
  AMYS: num(import.meta.env.VITE_AMYS_SURVEY_CHAT_ENTRY_ID, 161), // Amy's × Albertsons
} as const;
